import { Row, Col, Card, Statistic, Typography, Button, List, Tag, Spin, Empty } from 'antd';
import {
  PlusOutlined,
  SmileOutlined,
  LineChartOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@tradermind/shared/api';
import { useAuthStore } from '@tradermind/shared/stores';
import { formatCurrency, formatRelativeTime } from '@tradermind/shared/utils';
import { getEmotionById } from '@tradermind/shared/constants';

const { Title, Text } = Typography;

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { t: tEmotion } = useTranslation('emotion');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.getDashboard,
  });

  const dashboard = data?.data;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greeting.morning');
    if (hour < 18) return t('greeting.afternoon');
    return t('greeting.evening');
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 兼容两种数据结构：weekly_stats 嵌套对象或直接在 dashboard 下的扁平字段
  // 使用 any 类型以兼容后端返回的不同数据结构
  const stats: any = dashboard?.weekly_stats || dashboard;
  const todayEmotion = dashboard?.today_emotion;
  const emotionInfo = todayEmotion ? getEmotionById(todayEmotion.emotion_type) : null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          {getGreeting()}，{user?.nickname || t('trader')}
        </Title>
        <Text type="secondary">{t('subtitle')}</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.total_trades')}
              value={stats?.total_trades || 0}
              suffix={t('stats.trades_unit')}
              valueStyle={{ color: '#3B82F6' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.total_profit_loss')}
              value={stats?.total_profit_loss || 0}
              precision={2}
              prefix="$"
              valueStyle={{
                color: (stats?.total_profit_loss || 0) >= 0 ? '#10B981' : '#EF4444',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.total_commission')}
              value={(stats as any)?.total_commission || 0}
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.win_rate')}
              value={stats?.win_rate || 0}
              precision={1}
              suffix="%"
              valueStyle={{
                color: (stats?.win_rate || 0) >= 50 ? '#10B981' : '#EF4444',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.win_loss_ratio')}
              value={stats?.win_loss_ratio || 0}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('stats.profit_factor')}
              value={stats?.profit_factor || 0}
              precision={2}
              valueStyle={{
                color: (stats?.profit_factor || 0) >= 1 ? '#10B981' : '#EF4444',
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={t('today_emotion.title')}
            extra={
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => navigate('/emotions/new')}
              >
                {t('today_emotion.record')}
              </Button>
            }
          >
            {todayEmotion ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>{emotionInfo?.emoji}</div>
                <Tag color={emotionInfo?.color} style={{ fontSize: 16, padding: '4px 16px' }}>
                  {emotionInfo?.label ? tEmotion(emotionInfo.label) : ''}
                </Tag>
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">{t('today_emotion.intensity')}: {todayEmotion.intensity}/10</Text>
                </div>
              </div>
            ) : (
              <Empty
                image={<SmileOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                description={t('today_emotion.no_record')}
              >
                <Button type="primary" onClick={() => navigate('/emotions/new')}>
                  {t('today_emotion.record')}
                </Button>
              </Empty>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('quick_actions_section.title')}>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Button
                  type="default"
                  icon={<LineChartOutlined />}
                  size="large"
                  block
                  onClick={() => navigate('/trades/new')}
                  style={{ height: 80, flexDirection: 'column' }}
                >
                  <div>{t('quick_actions_section.record_trade')}</div>
                </Button>
              </Col>
              <Col span={8}>
                <Button
                  type="default"
                  icon={<SmileOutlined />}
                  size="large"
                  block
                  onClick={() => navigate('/emotions/new')}
                  style={{ height: 80, flexDirection: 'column' }}
                >
                  <div>{t('quick_actions_section.record_emotion')}</div>
                </Button>
              </Col>
              <Col span={8}>
                <Button
                  type="default"
                  icon={<CheckSquareOutlined />}
                  size="large"
                  block
                  onClick={() => navigate('/checklist/pre-trade')}
                  style={{ height: 80, flexDirection: 'column' }}
                >
                  <div>{t('quick_actions_section.pre_trade_check')}</div>
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={t('recent_trades_section.title')}
            extra={
              <Button type="link" onClick={() => navigate('/trades')}>
                {t('recent_trades_section.view_all')}
              </Button>
            }
          >
            <List
              dataSource={dashboard?.recent_trades || []}
              locale={{ emptyText: t('recent_trades_section.no_trades') }}
              renderItem={(trade) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/trades/${trade.id}`)}
                >
                  <List.Item.Meta
                    title={
                      <span>
                        {trade.symbol}{' '}
                        <Tag color={trade.direction === 'long' ? 'green' : 'red'}>
                          {trade.direction === 'long' ? t('recent_trades_section.long') : t('recent_trades_section.short')}
                        </Tag>
                      </span>
                    }
                    description={formatRelativeTime(trade.close_time)}
                  />
                  <div>
                    <Text
                      style={{
                        color: trade.profit_loss >= 0 ? '#10B981' : '#EF4444',
                        fontWeight: 600,
                      }}
                    >
                      {trade.profit_loss >= 0 ? '+' : ''}
                      {formatCurrency(trade.profit_loss)}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('insights.title')}>
            <List
              dataSource={dashboard?.insights || []}
              locale={{ emptyText: t('insights.no_insights') }}
              renderItem={(insight) => (
                <List.Item
                  actions={
                    insight.action
                      ? [
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(insight.action!.route)}
                          >
                            {insight.action.label}
                          </Button>,
                        ]
                      : undefined
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          marginTop: 8,
                          background:
                            insight.type === 'positive'
                              ? '#10B981'
                              : insight.type === 'warning'
                              ? '#F59E0B'
                              : '#3B82F6',
                        }}
                      />
                    }
                    title={insight.title}
                    description={insight.description}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
